import qrcode from 'qrcode-generator';
import { useEffect, useMemo, useState } from 'react';
import { endpoints } from '../services/api';
import { isLicensed, useSession } from '../store/session';

/**
 * QR code that opens checkout for this account on a phone – TV browsers can't drive
 * Stripe's payment page with a remote. While shown, it watches for the licence to arrive.
 */
export function PayOnPhone() {
  const setUser = useSession((s) => s.setUser);
  const [link, setLink] = useState<{ url: string; expiresAt: string } | null>(null);
  const [failed, setFailed] = useState(false);

  // Fetch a fresh link now and shortly before each one expires.
  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    const fetchLink = () =>
      endpoints
        .phoneLink()
        .then((l) => {
          if (cancelled) return;
          setLink(l);
          setFailed(false);
          timer = window.setTimeout(fetchLink, Math.max(60_000, new Date(l.expiresAt).getTime() - Date.now() - 2 * 60_000));
        })
        .catch(() => !cancelled && setFailed(true));
    void fetchLink();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, []);

  // Watch for the payment completing on the phone.
  useEffect(() => {
    const poll = window.setInterval(async () => {
      const me = await endpoints.me().catch(() => null);
      if (me?.user && isLicensed(me.user)) setUser(me.user);
    }, 4000);
    return () => window.clearInterval(poll);
  }, [setUser]);

  const qr = useMemo(() => {
    if (!link) return null;
    const q = qrcode(0, 'M');
    q.addData(link.url);
    q.make();
    return q.createDataURL(6, 2);
  }, [link]);

  if (failed) return <p className="muted text-sm">Couldn’t create a phone link – check the connection.</p>;
  return (
    <div className="flex items-center gap-4 rounded-lg border border-[var(--border)] p-3">
      {qr ? (
        <img src={qr} alt="QR code to buy the licence on your phone" className="h-32 w-32 shrink-0 rounded-lg bg-white p-1.5" />
      ) : (
        <div className="h-32 w-32 shrink-0 animate-pulse rounded-lg bg-[var(--border)]" aria-hidden />
      )}
      <div className="text-sm">
        <p className="font-medium">Pay on your phone</p>
        <p className="muted mt-1">
          Scan with your phone’s camera to pay securely with Stripe – no need to sign in there. This screen goes ad-free as soon as the
          payment is done.
        </p>
      </div>
    </div>
  );
}
